import React, { useCallback, useRef, useState } from "react";
import { NextSeo } from "next-seo";
import GenericFormDisplay from "domains/3d/components/GenericFormDisplay";
import { useCustomInference } from "domains/3d/hooks/useCustomInference";
import useImportParamsCustomInference from "domains/3d/hooks/useImportParamsCustomInference";
import { CardActions3D } from "domains/assets/components/CardActions";
import { ListActions3D } from "domains/assets/components/ListActions";
import FileManagerImage from "domains/file-manager/components/FileManagerImage";
import { useGetFileImagePlaceholders } from "domains/file-manager/hooks/useGetFileImagePlaceholders";
import InferenceGeneratorContent, {
  InferenceGeneratorTabsHandle,
} from "domains/inference/components/InferenceGenerator/Content";
import SelectModel from "domains/inference/components/InferenceGenerator/Sidebar/SelectModel";
import { FM_HEADER_TOP_0 } from "domains/inference/constants/fileManagerStyle";
import { SectionsProvider } from "domains/inference/contexts/SectionsProvider";
import {
  JobSessionProvider,
  useJobSessionContext,
} from "domains/jobs/contexts/JobSessionProviderV2";
import Divider from "domains/ui/components/Divider";
import {
  GeneratorContainer,
  GeneratorContent,
  GeneratorSidebar,
} from "domains/ui/components/GeneratorLayout";
import SidebarTitle from "domains/ui/components/Sidebar/SidebarTitle";

export default function Generate3DPage() {
  return (
    <>
      <NextSeo title="3D" />

      <SectionsProvider>
        <JobSessionProvider
          forcedFilterTypeValues={["3d:inference"]}
          placeholderAssetType="img23d"
        >
          <Generate3D />
        </JobSessionProvider>
      </SectionsProvider>
    </>
  );
}

// ------------------------------------

export function Generate3D() {
  const {
    handleGenerate,
    isGenerating,
    isDisabled,
    cuCost,
    isCuLoading,
    form,
    setForm,
    model,
    setModel,
    disabledReasons,
  } = useCustomInference({
    type: "3d",
  });
  const { files, jobs, isLoading, fileManagerProps, addJob } =
    useJobSessionContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<InferenceGeneratorTabsHandle>(null);
  const [selectModelIsOpen, setSelectModelIsOpen] = useState(false);

  const { hasFailedToLoadModel } = useImportParamsCustomInference({
    model,
    setModel,
    setForm,
    type: "3d",
  });

  const onSubmit = useCallback(async () => {
    const job = await handleGenerate();
    if (job) {
      addJob(job);
      tabsRef.current?.reset();
    }
  }, [handleGenerate, addJob]);

  // ----------------------------------

  const placeholders = useGetFileImagePlaceholders({
    length: 1,
    dimensions: {
      width: 300,
      height: 200,
    },
    hideDimensions: true,
  });

  // ----------------------------------

  return (
    <GeneratorContainer onSubmit={onSubmit}>
      <GeneratorSidebar>
        <SidebarTitle title="Generate 3D" />
        <Divider />

        <SelectModel
          variant="3d"
          isOpen={selectModelIsOpen}
          setIsOpen={setSelectModelIsOpen}
          model={model}
          setModel={setModel}
          isLoadingModel={!model && !hasFailedToLoadModel}
        />
        <Divider />

        <GenericFormDisplay
          form={form}
          setForm={setForm}
          model={model}
          triggerButtonProps={{
            onClick: onSubmit,
            isDisabled: isDisabled,
            isCuLoading: isCuLoading,
            cuCost: cuCost,
            isGenerating: isGenerating,
          }}
          type="3d"
          disabledReasons={disabledReasons}
          isLoadingModel={!model && !hasFailedToLoadModel}
        />
      </GeneratorSidebar>

      <GeneratorContent scrollRef={scrollRef}>
        <InferenceGeneratorContent
          type="3d"
          scrollRef={scrollRef}
          tabsRef={tabsRef}
          CardActionsComponent={CardActions3D}
          suggestArgs={model ? { model } : undefined}
        >
          <FileManagerImage
            variant="3d"
            scrollRef={scrollRef}
            files={
              !files?.length && !jobs?.length && !isLoading
                ? placeholders
                : files
            }
            jobs={jobs}
            isSelectable
            isLoading={isLoading}
            {...fileManagerProps}
            ListActionsComponent={ListActions3D}
            CardActionsComponent={CardActions3D}
            styleProps={FM_HEADER_TOP_0}
          />
        </InferenceGeneratorContent>
      </GeneratorContent>
    </GeneratorContainer>
  );
}
